/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { signOut } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

import { auth, db } from "../firebase";
import { getAdminBrandVars } from "../utils/adminBranding";
import { useAuth } from "../contexts/AuthContext";
import AdminBehaviourDashboard from "../components/admin/AdminBehaviourDashboard";
import OverviewSection from "../components/admin/OverviewSection";
import StudentsSection from "../components/admin/StudentsSection";
import ClassesSection from "../components/admin/ClassesSection";
import TeachersSection from "../components/admin/TeachersSection";
import ParentsSection from "../components/admin/ParentsSection";
import AttendanceControlSection from "../components/admin/AttendanceControlSection";
import SettingsSection from "../components/admin/SettingsSection";
import InviteDashboardSection from "../components/admin/InviteDashboardSection";
import ParentInviteDashboard from "../pages/ParentInviteDashboard";
import AdminBroadcastModal from "../components/admin/AdminBroadcastModal";
import AdminsSection from "../components/admin/AdminsSection";
import AbsenceSection from "../components/admin/AbsenceSection";
import AuditDashboard from "../components/admin/AuditDashboard";
import SecurityCommandCentre from "../components/admin/SecurityCommandCentre";
import {
  AdminPageMotion,
  MotionButton,
  SidebarItemMotion,
} from "../components/motion/AdminMotion";

const ADMIN_SECTIONS = [
  { key: "overview", label: "Overview", icon: "◫" },
  { key: "students", label: "Students", icon: "👨‍🎓" },
  { key: "classes", label: "Classes", icon: "▦" },
  { key: "teachers", label: "School Staff Portal", icon: "👩‍🏫" },
  { key: "parents", label: "Parents", icon: "👨‍👩‍👧" },
  { key: "parent-invites", label: "Parent Invites", icon: "✉" },
  { key: "attendance-control", label: "Attendance Control", icon: "✓" },
  { key: "absence", label: "Absence", icon: "📝" },
  { key: "behaviour-dashboard", label: "Behaviour Dashboard", icon: "⚑" },
  { key: "invite-dashboard", label: "Invite Dashboard", icon: "⌁" },
  { key: "audit-dashboard", label: "Audit Dashboard", icon: "📋" },
  { key: "security-centre", label: "Security Centre", icon: "🔐" },
  { key: "settings", label: "Settings", icon: "⚙" },
];

function formatNotificationTime(value) {
  try {
    const date = value?.toDate ? value.toDate() : value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return "Just now";

    const diffMs = Date.now() - date.getTime();
    const mins = Math.max(0, Math.floor(diffMs / 60000));

    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;

    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  } catch {
    return "Just now";
  }
}

export default function AdminManager() {
  const navigate = useNavigate();
  const { profile } = useAuth() || {};
  const schoolId = profile?.schoolId || "school_001";

  const [searchParams, setSearchParams] = useSearchParams();
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);

  const [schoolBranding, setSchoolBranding] = useState({
    schoolName: "Your School",
    logoUrl: "",
    primaryColor: "#4f46e5",
  });

  const [classes, setClasses] = useState([]);
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [showNotificationCentre, setShowNotificationCentre] = useState(false);

  const validSections = useMemo(
    () => ADMIN_SECTIONS.map((section) => section.key),
    []
  );

  const getSectionFromUrl = () => {
    const tab = String(searchParams.get("tab") || "overview").toLowerCase();
    return validSections.includes(tab) ? tab : "overview";
  };

  const [active, setActive] = useState(getSectionFromUrl);

  useEffect(() => {
    const next = getSectionFromUrl();
    setActive(next);
  }, [searchParams]);

  useEffect(() => {
    async function loadSchoolBranding() {
      if (!schoolId) return;

      try {
        const snap = await getDoc(
          doc(db, "schools", schoolId, "settings", "school")
        );

        if (snap.exists()) {
          const data = snap.data() || {};
          setSchoolBranding({
            schoolName: data.schoolName || data.name || "Your School",
            logoUrl: data.logoUrl || "",
            primaryColor: data.primaryColor || "#4f46e5",
          });
        } else {
          setSchoolBranding({
            schoolName: "Your School",
            logoUrl: "",
            primaryColor: "#4f46e5",
          });
        }
      } catch (err) {
        console.error("Failed to load admin school branding:", err);
      }
    }

    loadSchoolBranding();
  }, [schoolId]);


  useEffect(() => {
    if (!schoolId) {
      setAdminNotifications([]);
      return undefined;
    }

    const notificationQuery = query(
      collection(db, "schools", schoolId, "adminNotifications"),
      orderBy("createdAt", "desc"),
      limit(30)
    );

    const unsubscribe = onSnapshot(
      notificationQuery,
      (snapshot) => {
        const rows = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        setAdminNotifications(rows);
      },
      (err) => {
        console.error("Failed to load admin notifications:", err);
        setAdminNotifications([]);
      }
    );

    return unsubscribe;
  }, [schoolId]);

  const unreadNotificationCount = useMemo(
    () =>
      adminNotifications.filter(
        (item) =>
          String(item.status || "unread").toLowerCase() === "unread" ||
          String(item.status || "").toLowerCase() === "pending"
      ).length,
    [adminNotifications]
  );

  const hasNotifications = adminNotifications.length > 0;

  async function markAdminNotificationRead(notification) {
    if (!schoolId || !notification?.id) return;

    try {
      await updateDoc(
        doc(db, "schools", schoolId, "adminNotifications", notification.id),
        {
          status:
            String(notification.status || "").toLowerCase() === "resolved"
              ? "resolved"
              : "read",
          readAt: serverTimestamp(),
        }
      );
    } catch (err) {
      console.error("Could not mark admin notification as read:", err);
    }
  }

  async function handleReviewNotification(notification) {
    if (!notification) return;

    await markAdminNotificationRead(notification);
    setShowNotificationCentre(false);

    if (notification.type === "absence_report_submitted") {
      const reportId =
        notification.absenceReportId ||
        notification.absenceId ||
        notification.reportId ||
        notification.targetId ||
        notification.relatedId ||
        notification.sourceId ||
        "";

      setActive("absence");

      setSearchParams({
        tab: "absence",
        reportId,
        absenceId: reportId,
        focus: reportId,
        status: "all",
      });

      window.setTimeout(() => {
        if (!reportId) return;

        window.dispatchEvent(
          new CustomEvent("presentplus:openAbsenceReport", {
            detail: {
              absenceId: reportId,
              reportId,
              id: reportId,
            },
          })
        );
      }, 250);

      return;
    }

    const notificationType = String(notification.type || "").toLowerCase();
    const attendanceStatus =
      notificationType === "attendance_reopen_request"
        ? "reopen_requested"
        : "issues";

    setActive("attendance-control");
    setSearchParams({
      tab: "attendance-control",
      registerId: notification.registerId || "",
      dateKey: notification.dateKey || "",
      classId: notification.classId || "",
      status: attendanceStatus,
    });
  }

  const schoolName = schoolBranding.schoolName || "Your School";
  const brandPrimary = schoolBranding.primaryColor || "#4f46e5";
  const brandVars = useMemo(() => getAdminBrandVars(brandPrimary), [brandPrimary]);

  const setSection = (key) => {
    setActive(key);
    setSearchParams({ tab: key });
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  const renderSection = () => {
    switch (active) {
      case "students":
        return <StudentsSection schoolId={schoolId} />;

      case "classes":
        return <ClassesSection schoolId={schoolId} setClasses={setClasses} />;

        case "admins":
  return <AdminsSection schoolId={schoolId} />;

      case "teachers":
        return <TeachersSection schoolId={schoolId} />;

      case "parents":
        return <ParentsSection schoolId={schoolId} />;

      case "parent-invites":
        return <ParentInviteDashboard schoolId={schoolId} />;

      case "attendance-control":
        return <AttendanceControlSection schoolId={schoolId} />;

        case "absence":
  return <AbsenceSection schoolId={schoolId} mode="admin" />;

      case "behaviour-dashboard":
        return (
          <AdminBehaviourDashboard
            schoolId={schoolId}
            classes={classes}
            schoolName={schoolName}
            brandPrimary={brandPrimary}
          />
        );

      case "invite-dashboard":
        return <InviteDashboardSection schoolId={schoolId} />;

     case "audit-dashboard":
  return <AuditDashboard schoolId={schoolId} />;

case "security-centre":
  return <SecurityCommandCentre schoolId={schoolId} />;

      case "settings":
        return <SettingsSection schoolId={schoolId} />;

      case "overview":
      default:
        return (
          <OverviewSection
            schoolId={schoolId}
            onNavigate={(key) => setSection(key)}
          />
        );
    }
  };

  return (
    <>
      <style>{`
        .pp-sidebar-scroll {
          overflow-y: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .pp-sidebar-scroll::-webkit-scrollbar {
          width: 0;
          height: 0;
          display: none;
        }

        button:focus-visible,
        input:focus-visible,
        select:focus-visible,
        textarea:focus-visible {
          outline: 3px solid rgba(79, 99, 255, 0.28);
          outline-offset: 2px;
        }
      `}</style>

      <div
  className="pp-admin-fixed-shell"
  style={{
    ...styles.page,
    ...brandVars,
  }}
>
        <aside className="pp-admin-sidebar" style={styles.sidebar}>
          <div style={styles.sidebarTop}>
            <div style={styles.brandWrap}>
              {schoolBranding.logoUrl ? (
                <img
                  src={schoolBranding.logoUrl}
                  alt={schoolName}
                  style={styles.brandLogo}
                />
              ) : (
                <div
                  style={{
                    ...styles.brandBadge,
                    background: "var(--pp-brand-gradient)",
                  }}
                >
                  {String(schoolName || "S").trim().charAt(0).toUpperCase()}
                </div>
              )}

              <div>
                <div style={styles.brandEyebrow}>{schoolName}</div>
                <div style={styles.brandTitle}>Admin Console</div>
              </div>
            </div>

            <div style={styles.schoolCard}>
              <div style={styles.schoolLabel}>School</div>
              <div style={styles.schoolValue}>{schoolName}</div>
            </div>
          </div>

          <div className="pp-sidebar-scroll" style={styles.navScroll}>
            <nav style={styles.nav}>
              {ADMIN_SECTIONS.map((item, index) => {
                const isActive = item.key === active;

                return (
                  <SidebarItemMotion
                    key={item.key}
                    isActive={isActive}
                    onClick={() => setSection(item.key)}
                    style={{
                      ...styles.navButton,
                      ...(isActive ? styles.navButtonActive : {}),
                      animationDelay: `${index * 20}ms`,
                    }}
                  >
                    <span
                      style={{
                        ...styles.navIcon,
                        ...(isActive ? styles.navIconActive : {}),
                      }}
                    >
                      {item.icon}
                    </span>
                    <span style={styles.navText}>{item.label}</span>
                  </SidebarItemMotion>
                );
              })}
            </nav>
          </div>

          <div style={styles.sidebarFooter}>
            <MotionButton
              onClick={() => setShowBroadcastModal(true)}
              style={styles.broadcastButton}
            >
              Send Broadcast
            </MotionButton>

            <MotionButton onClick={handleLogout} style={styles.logoutButton}>
              Logout
            </MotionButton>
          </div>
        </aside>

        <main className="pp-admin-scroll-main" style={styles.main}>
          <div style={styles.commandBar}>
            <div>
              <div style={styles.commandEyebrow}>Command centre</div>
              <div style={styles.commandText}>
                Live operational alerts for attendance, requests and admin actions.
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowNotificationCentre(true)}
              style={{
                ...styles.notificationButton,
                ...(unreadNotificationCount ? styles.notificationButtonActive : {}),
              }}
              aria-label="Open admin notifications"
            >
              <span style={styles.notificationBell}>🔔</span>
              <span>Notifications</span>
              {unreadNotificationCount ? (
                <span style={styles.notificationBadge}>
                  {unreadNotificationCount}
                </span>
              ) : null}
            </button>
          </div>

          <div className="admin-section-frame">{renderSection()}</div>
        </main>
      </div>

      {showNotificationCentre ? (
        <div
          style={styles.notificationOverlay}
          onClick={() => setShowNotificationCentre(false)}
        >
          <div
            style={styles.notificationPanel}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={styles.notificationPanelHead}>
              <div>
                <div style={styles.notificationPanelEyebrow}>
                  PresentPlus Admin
                </div>
                <h3 style={styles.notificationPanelTitle}>
                  Notification Centre
                </h3>
                <p style={styles.notificationPanelText}>
                  Review attendance requests and operational alerts from your school.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowNotificationCentre(false)}
                style={styles.notificationClose}
                aria-label="Close admin notifications"
              >
                ×
              </button>
            </div>

            <div style={styles.notificationList}>
              {hasNotifications ? (
                adminNotifications.map((item) => {
                  const unread =
                    String(item.status || "unread").toLowerCase() === "unread" ||
                    String(item.status || "").toLowerCase() === "pending";

                  return (
                    <div
                      key={item.id}
                      style={{
                        ...styles.notificationItem,
                        ...(unread ? styles.notificationItemUnread : {}),
                      }}
                    >
                      <div style={styles.notificationItemIcon}>
                        {item.type === "attendance_reopen_request" ? "✓" : item.type === "absence_report_submitted" ? "📄" : "!"}
                      </div>

                      <div style={styles.notificationItemBody}>
                        <div style={styles.notificationItemTop}>
                          <strong>{item.title || "Admin notification"}</strong>
                          <span>{formatNotificationTime(item.createdAt)}</span>
                        </div>

                        <p style={styles.notificationMessage}>
                          {item.message || "A new admin action needs review."}
                        </p>

                        {item.reason ? (
                          <div style={styles.notificationReason}>
                            “{item.reason}”
                          </div>
                        ) : null}

                        <div style={styles.notificationMeta}>
                          {item.className ? <span>{item.className}</span> : null}
                          {item.teacherName ? <span>{item.teacherName}</span> : null}
                          {item.dateKey ? <span>{item.dateKey}</span> : null}
                        </div>

                        {item.type === "attendance_reopen_request" ||
                        item.type === "absence_report_submitted" ? (
                          <button
                            type="button"
                            onClick={() => handleReviewNotification(item)}
                            style={styles.notificationReviewButton}
                          >
                            {item.type === "absence_report_submitted"
                              ? "Review absence →"
                              : "Review request →"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => markAdminNotificationRead(item)}
                            style={styles.notificationReviewButton}
                          >
                            Mark as read
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={styles.notificationEmpty}>
                  <strong>All clear.</strong>
                  <span>No admin notifications yet.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {showBroadcastModal ? (
        <AdminBroadcastModal onClose={() => setShowBroadcastModal(false)} />
      ) : null}
    </>
  );
}

const styles = {
  page: {
    width: "100%",
    height: "100vh",
    minHeight: "100vh",
    display: "flex",
    overflow: "hidden",
    background:
      "linear-gradient(180deg, #f4f7ff 0%, #eef3ff 34%, #f8faff 100%)",
    color: "#112042",
  },

  sidebar: {
    width: 284,
    minWidth: 284,
    background: "rgba(10, 20, 52, 0.97)",
    color: "#ffffff",
    padding: "22px 16px 18px",
    display: "flex",
    flexDirection: "column",
    borderRight: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "20px 0 60px rgba(10, 20, 52, 0.18)",
    position: "relative",
    top: 0,
    height: "100vh",
    maxHeight: "100vh",
    overflow: "hidden",
  },

  sidebarTop: {
    flexShrink: 0,
  },

  brandWrap: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 18,
    padding: "4px 8px 12px",
  },

  brandBadge: {
    width: 44,
    height: 44,
    borderRadius: 14,
    display: "grid",
    placeItems: "center",
    fontWeight: 800,
    fontSize: 15,
    color: "#fff",
    boxShadow: "0 18px 40px var(--pp-brand-glow)",
    flexShrink: 0,
  },

  brandLogo: {
    width: 44,
    height: 44,
    borderRadius: 14,
    objectFit: "cover",
    background: "#fff",
    padding: 4,
    flexShrink: 0,
  },

  brandEyebrow: {
    fontSize: 11,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    opacity: 0.72,
    marginBottom: 2,
  },

  brandTitle: {
    fontSize: 20,
    fontWeight: 800,
    letterSpacing: "-0.02em",
  },

  schoolCard: {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 18,
    padding: "14px 14px 12px",
    marginBottom: 18,
  },

  schoolLabel: {
    fontSize: 11,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    opacity: 0.7,
    marginBottom: 6,
  },

  schoolValue: {
    fontSize: 15,
    fontWeight: 700,
    wordBreak: "break-word",
  },

  navScroll: {
    flex: 1,
    minHeight: 0,
    paddingRight: 2,
  },

  nav: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  navButton: {
    appearance: "none",
    border: "1px solid transparent",
    background: "transparent",
    color: "#dbe4ff",
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    textAlign: "left",
    padding: "14px 12px",
    borderRadius: 18,
    cursor: "pointer",
    fontSize: 15,
    fontWeight: 700,
  },

navButtonActive: {
  background: "var(--pp-brand-gradient)",
  color: "#ffffff",
  boxShadow: "0 18px 36px var(--pp-brand-glow)",
},
  navIcon: {
    width: 32,
    height: 32,
    borderRadius: 10,
    display: "grid",
    placeItems: "center",
    background: "rgba(255,255,255,0.06)",
    fontSize: 14,
    flexShrink: 0,
  },

  navIconActive: {
    background: "rgba(255,255,255,0.18)",
  },

  navText: {
    lineHeight: 1.2,
  },

  sidebarFooter: {
    paddingTop: 14,
    marginTop: 14,
    borderTop: "1px solid rgba(255,255,255,0.08)",
    flexShrink: 0,
    display: "grid",
    gap: 10,
  },

broadcastButton: {
  width: "100%",
  border: "none",
  borderRadius: 18,
  padding: "16px 16px",
  fontSize: 16,
  fontWeight: 800,
  cursor: "pointer",
  color: "#fff",
  background: "var(--pp-brand-gradient)",
  boxShadow: "0 18px 36px var(--pp-brand-glow)",
},

  logoutButton: {
    width: "100%",
    borderRadius: 18,
    padding: "14px 16px",
    fontSize: 15,
    fontWeight: 800,
    cursor: "pointer",
    color: "#dbe4ff",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.08)",
  },

  main: {
    flex: 1,
    minWidth: 0,
    height: "100vh",
    maxHeight: "100vh",
    overflowY: "auto",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
    padding: "24px 24px 40px",
    boxSizing: "border-box",
  },


  commandBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 18,
    background: "rgba(255,255,255,0.74)",
    border: "1px solid rgba(199,210,254,0.72)",
    borderRadius: 24,
    padding: "14px 16px",
    boxShadow: "0 16px 38px rgba(15, 23, 42, 0.06)",
    backdropFilter: "blur(18px)",
  },

  commandEyebrow: {
    fontSize: 10,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "#2563eb",
    fontWeight: 900,
    marginBottom: 4,
  },

  commandText: {
    fontSize: 13,
    color: "#64748b",
    fontWeight: 700,
  },

  notificationButton: {
    border: "1px solid rgba(199,210,254,0.95)",
    background: "#ffffff",
    color: "#172554",
    borderRadius: 18,
    padding: "12px 14px",
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    fontSize: 14,
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
    position: "relative",
  },

  notificationButtonActive: {
    background: "var(--pp-brand-gradient)",
    color: "#ffffff",
    border: "1px solid transparent",
    boxShadow: "0 18px 36px var(--pp-brand-glow)",
  },

  notificationBell: {
    fontSize: 16,
    lineHeight: 1,
  },

  notificationBadge: {
    minWidth: 24,
    height: 24,
    borderRadius: 999,
    background: "#ef4444",
    color: "#ffffff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 7px",
    fontSize: 12,
    fontWeight: 950,
    boxShadow: "0 10px 20px rgba(239, 68, 68, 0.25)",
  },

  notificationOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 80,
    background: "rgba(15, 23, 42, 0.48)",
    backdropFilter: "blur(10px)",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    padding: "24px",
  },

  notificationPanel: {
    width: "min(520px, calc(100vw - 32px))",
    maxHeight: "calc(100vh - 48px)",
    overflow: "hidden",
    background: "#ffffff",
    borderRadius: 28,
    border: "1px solid rgba(226,232,240,0.9)",
    boxShadow: "0 36px 90px rgba(15, 23, 42, 0.26)",
    display: "flex",
    flexDirection: "column",
  },

  notificationPanelHead: {
    padding: 22,
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    borderBottom: "1px solid #eef2ff",
  },

  notificationPanelEyebrow: {
    fontSize: 11,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "#2563eb",
    fontWeight: 950,
    marginBottom: 6,
  },

  notificationPanelTitle: {
    margin: 0,
    fontSize: 24,
    letterSpacing: "-0.04em",
    color: "#0f172a",
    fontWeight: 950,
  },

  notificationPanelText: {
    margin: "8px 0 0",
    fontSize: 14,
    color: "#64748b",
    lineHeight: 1.55,
    fontWeight: 650,
  },

  notificationClose: {
    width: 42,
    height: 42,
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    color: "#0f172a",
    cursor: "pointer",
    fontSize: 26,
    lineHeight: 1,
  },

  notificationList: {
    padding: 16,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  notificationItem: {
    display: "flex",
    gap: 12,
    padding: 14,
    borderRadius: 20,
    border: "1px solid #e5e7eb",
    background: "#ffffff",
  },

  notificationItemUnread: {
    background: "linear-gradient(180deg,#eff6ff 0%,#ffffff 100%)",
    border: "1px solid #bfdbfe",
  },

  notificationItemIcon: {
    width: 38,
    height: 38,
    borderRadius: 14,
    display: "grid",
    placeItems: "center",
    color: "#ffffff",
    background: "var(--pp-brand-gradient)",
    fontWeight: 950,
    flexShrink: 0,
  },

  notificationItemBody: {
    flex: 1,
    minWidth: 0,
  },

  notificationItemTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    color: "#0f172a",
    fontSize: 14,
  },

  notificationMessage: {
    margin: "8px 0 0",
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.45,
    fontWeight: 700,
  },

  notificationReason: {
    marginTop: 10,
    padding: "10px 12px",
    borderRadius: 14,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#334155",
    fontSize: 13,
    lineHeight: 1.45,
    fontWeight: 700,
  },

  notificationMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 10,
  },

  notificationReviewButton: {
    marginTop: 12,
    border: "none",
    borderRadius: 14,
    background: "#0f49bc",
    color: "#ffffff",
    padding: "10px 12px",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },

  notificationEmpty: {
    minHeight: 180,
    borderRadius: 22,
    display: "grid",
    placeItems: "center",
    textAlign: "center",
    gap: 4,
    background: "#f8fafc",
    color: "#64748b",
    fontWeight: 750,
  },

};